import { Fragment, useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import TaskInstructionHint from "@/components/storyboard/TaskInstructionHint";
import { GradedFeedback } from "@/components/storyboard/GradedFeedback";
import { QuizActionButton } from "@/components/storyboard/quiz/QuizActionButton";
import { QuizAttemptHint } from "@/components/storyboard/quiz/QuizAttemptHint";
import QuizEntryCounter from "@/components/storyboard/quiz/QuizEntryCounter";
import { getTextParts } from "@/components/storyboard/quiz/utils/quizUtils";
import { buildClozeItem } from "@/components/storyboard/quiz/review/buildReviewItem";
import {
  attemptsLeft,
  canRetry,
  NO_ATTEMPTS_LEFT_FEEDBACK,
  retryLabel,
  ValidationAttemptsExceededError,
} from "@/lib/validationSubmission";
import type { ValidationResult } from "@/services/QuizValidationService";
import type { QuizGapwordFreeTextEntryData } from "@/types/storyboard";
import type { InteractiveElementScore } from "@/lib/interactiveElementState";
import type { QuizReview, QuizReviewItem } from "@/lib/quizReview";
import {
  advanceAfterCollect,
  collectsWithoutVerdict,
  type HiddenVerdictProps,
} from "@/lib/quizCollect";

export interface QuizGapwordFreeTextState {
  currentIndex: number;
  userAnswers: string[];
  isSubmitted: boolean;
  isCorrect: boolean | null;
  correctCount: number;
  gapCorrect: boolean | null;
  /** Normalized four-level grade key from the LLM validation, when available. */
  grade?: string;
  /**
   * Server-counted submissions per entry index. Kept per entry because each
   * question is its own quiz slot with its own budget.
   */
  attemptCounts?: Record<number, number>;
  /** Attempt cap of this quiz type, null when it allows unlimited retries. */
  maxAttempts?: number | null;
}

export interface QuizGapwordFreeTextProps extends HiddenVerdictProps {
  entries: QuizGapwordFreeTextEntryData[];
  onValidate: (
    answers: string[],
    entryIndex: number
  ) => Promise<ValidationResult>;
  onComplete?: (score: InteractiveElementScore, review: QuizReview) => void;
  initialPartial?: QuizGapwordFreeTextState;
  initialIndex?: number;
  onPartialChange?: (partial: QuizGapwordFreeTextState, index: number) => void;
}

/** Persisted partials from before the per-gap fix hold a single `userAnswer` string. */
const normalizePartial = (
  partial: QuizGapwordFreeTextState & { userAnswer?: string }
): QuizGapwordFreeTextState => ({
  ...partial,
  userAnswers:
    partial.userAnswers ?? (partial.userAnswer ? [partial.userAnswer] : []),
});

const QuizGapwordFreeText = ({
  entries,
  onValidate,
  onComplete,
  initialPartial,
  initialIndex,
  onPartialChange,
  revealFeedback,
  advanceLabel,
  onAdvance,
}: QuizGapwordFreeTextProps) => {
  const [state, setState] = useState<QuizGapwordFreeTextState>(() => {
    if (initialPartial) return normalizePartial(initialPartial);
    return {
      currentIndex: initialIndex ?? 0,
      userAnswers: [],
      isSubmitted: false,
      isCorrect: null,
      correctCount: 0,
      gapCorrect: null,
    };
  });
  const [feedback, setFeedback] = useState("");
  const [isValidating, setIsValidating] = useState(false);

  const hasInteractedRef = useRef<boolean>(initialPartial != null);
  const reviewRef = useRef<QuizReviewItem[]>([]);

  useEffect(() => {
    if (!hasInteractedRef.current) return;
    onPartialChange?.(state, state.currentIndex);
  }, [state, onPartialChange]);

  const { currentIndex, userAnswers, isSubmitted, isCorrect } = state;

  if (!entries || entries.length === 0) {
    return null;
  }

  const currentEntry = entries[currentIndex];

  if (!currentEntry) {
    return null;
  }

  const textParts = getTextParts(currentEntry.clozeText, currentEntry.gapMark);
  const numberOfGaps = textParts.length - 1;

  const attemptBudget = {
    attemptCount: state.attemptCounts?.[currentIndex],
    maxAttempts: state.maxAttempts,
  };
  // A wrong entry with attempts left stays open instead of moving on. Types
  // without a cap report no budget at all and remain single-shot.
  const retryable =
    isSubmitted && canRetry({ ...attemptBudget, correct: isCorrect });
  // Only once the budget is spent is the answer worth giving away.
  const revealSolution =
    isSubmitted && isCorrect !== true && attemptsLeft(attemptBudget) === 0;

  const hasAllAnswers =
    numberOfGaps > 0 &&
    Array.from({ length: numberOfGaps }, (_, i) => userAnswers[i]).every(
      (answer) => answer && answer.trim() !== ""
    );

  const handleInputChange = (gapIndex: number, value: string) => {
    if (isSubmitted) return;
    hasInteractedRef.current = true;
    setState((prev) => {
      const newAnswers = [...prev.userAnswers];
      newAnswers[gapIndex] = value;
      return { ...prev, userAnswers: newAnswers };
    });
  };

  const handleSubmit = async () => {
    if (!hasAllAnswers) return;

    const answers = Array.from(
      { length: numberOfGaps },
      (_, i) => userAnswers[i] ?? ""
    );

    if (collectsWithoutVerdict(revealFeedback)) {
      await advanceAfterCollect(
        () => onValidate(answers, currentIndex),
        onAdvance
      );
      return;
    }

    setIsValidating(true);
    try {
      const result = await onValidate(answers, currentIndex);
      const isEntryCorrect = result.correct === true;
      // A retry only ever follows a wrong answer, so a correct entry is counted
      // exactly once however many attempts it took to get there.
      const correctCount = state.correctCount + (isEntryCorrect ? 1 : 0);
      const entryRetryable = canRetry({ ...result, correct: result.correct });

      reviewRef.current[currentIndex] = buildClozeItem({
        prompt: currentEntry.instruction,
        clozeText: currentEntry.clozeText,
        gapMark: currentEntry.gapMark,
        gaps: answers.map((answer, i) => ({
          userAnswer: answer,
          correctAnswer:
            currentEntry.gaps[i]?.referenceAnswer.join(" / ") ?? "",
          correct: isEntryCorrect,
        })),
        isCorrect: isEntryCorrect,
        // Prefer the individual LLM feedback; the pre-written storyboard
        // feedback only remains as fallback for legacy responses without one.
        feedback: result.feedback?.trim()
          ? result.feedback
          : currentEntry.userFeedback,
      });

      hasInteractedRef.current = true;
      setState((prev) => ({
        ...prev,
        isSubmitted: true,
        isCorrect: isEntryCorrect,
        grade: result.grade,
        correctCount,
        gapCorrect: isEntryCorrect,
        attemptCounts: {
          ...prev.attemptCounts,
          ...(result.attemptCount == null
            ? {}
            : { [currentIndex]: result.attemptCount }),
        },
        maxAttempts: result.maxAttempts,
      }));
      setFeedback(result.feedback);

      // Scoring the quiz ends it, so the last entry waits until it has no
      // retry left — otherwise a first wrong answer would close the quiz out.
      if (currentIndex === entries.length - 1 && !entryRetryable) {
        onComplete?.(
          { correct: correctCount, total: entries.length },
          { items: reviewRef.current.filter(Boolean) }
        );
        if (result.correct) {
          confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
        }
      }
    } catch (error) {
      // The server is the authority on the budget: if it says the attempts are
      // gone, the entry stands as it is however many tries the UI believed it
      // had left.
      if (!(error instanceof ValidationAttemptsExceededError)) throw error;
      hasInteractedRef.current = true;
      setState((prev) => ({
        ...prev,
        isSubmitted: true,
        isCorrect: prev.isCorrect ?? false,
        gapCorrect: prev.gapCorrect ?? false,
        attemptCounts: {
          ...prev.attemptCounts,
          [currentIndex]: error.attemptCount,
        },
        maxAttempts: error.maxAttempts,
      }));
      setFeedback(NO_ATTEMPTS_LEFT_FEEDBACK);

      if (!reviewRef.current[currentIndex]) {
        reviewRef.current[currentIndex] = buildClozeItem({
          prompt: currentEntry.instruction,
          clozeText: currentEntry.clozeText,
          gapMark: currentEntry.gapMark,
          gaps: answers.map((answer, i) => ({
            userAnswer: answer,
            correctAnswer:
              currentEntry.gaps[i]?.referenceAnswer.join(" / ") ?? "",
            correct: false,
          })),
          isCorrect: false,
          feedback: NO_ATTEMPTS_LEFT_FEEDBACK,
        });
      }

      if (currentIndex === entries.length - 1) {
        onComplete?.(
          { correct: state.correctCount, total: entries.length },
          { items: reviewRef.current.filter(Boolean) }
        );
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleRetry = () => {
    // The answers stay put so the learner can correct them rather than retype.
    setState((prev) => ({
      ...prev,
      isSubmitted: false,
      isCorrect: null,
      gapCorrect: null,
      grade: undefined,
    }));
    setFeedback("");
  };

  const handleNext = () => {
    if (currentIndex < entries.length - 1) {
      setState((prev) => ({
        ...prev,
        currentIndex: prev.currentIndex + 1,
        userAnswers: [],
        isSubmitted: false,
        isCorrect: null,
        gapCorrect: null,
        grade: undefined,
      }));
      setFeedback("");
    }
  };

  const submitLabel = collectsWithoutVerdict(revealFeedback)
    ? (advanceLabel ?? "Nächste Frage")
    : "Überprüfen";

  /** Submit, then retry while the budget allows, then move on. */
  const renderAction = () => {
    if (!isSubmitted) {
      return (
        <QuizActionButton
          variant="default"
          onClick={handleSubmit}
          disabled={!hasAllAnswers || isValidating}
          withArrow={!isValidating}
          className={
            isValidating ? "opacity-80 animate-pulse cursor-wait" : undefined
          }
        >
          {isValidating ? (
            <span className="flex items-center gap-2">
              <Loader2 className="animate-spin w-5 h-5 shrink-0" />
              <span>Wird ausgewertet…</span>
            </span>
          ) : (
            submitLabel
          )}
        </QuizActionButton>
      );
    }
    if (retryable) {
      return (
        <QuizActionButton variant="default" onClick={handleRetry}>
          {retryLabel()}
        </QuizActionButton>
      );
    }
    if (currentIndex < entries.length - 1) {
      return (
        <QuizActionButton onClick={handleNext}>Nächste Frage</QuizActionButton>
      );
    }
    return null;
  };

  const getInputStyles = (gapIndex: number) => {
    if (isSubmitted) {
      // Neutral recap — GradedFeedback owns the verdict color.
      return "border-border bg-muted/40 text-foreground";
    }
    if (userAnswers[gapIndex]) {
      return "border-secondary bg-secondary/10";
    }
    return "border-gray-200 bg-white hover:border-gray-300";
  };

  /** Attempt banner first, then the verdict — same order as freetext/worksheet. */
  const renderSubmittedVerdict = () => {
    if (!isSubmitted || isCorrect === null) return null;

    const feedbackText =
      feedback ||
      (isCorrect
        ? currentEntry.userFeedback?.positive
        : currentEntry.userFeedback?.negative) ||
      "";

    return (
      <>
        {isCorrect !== true && (
          <QuizAttemptHint
            attemptCount={state.attemptCounts?.[currentIndex]}
            maxAttempts={state.maxAttempts}
            className="w-full max-w-2xl"
          />
        )}
        <GradedFeedback
          result={{
            correct: isCorrect,
            grade: state.grade,
            feedback: feedbackText,
          }}
          className="w-full max-w-2xl"
        />
      </>
    );
  };

  return (
    <div className="flex h-full flex-col">
      <QuizEntryCounter currentIndex={currentIndex} total={entries.length}>
        <div className="prose quiz-question max-w-full font-semibold text-primary">
          <CourseMarkdown>{currentEntry.instruction}</CourseMarkdown>
        </div>
      </QuizEntryCounter>

      <div className="flex min-w-0 w-full flex-1 flex-col gap-4 overflow-y-auto pb-4 pt-8">
        {renderSubmittedVerdict()}

        {revealSolution && (
          <div className="w-full max-w-2xl rounded-lg border border-border bg-muted/40 px-4 py-3">
            <p className="font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              {numberOfGaps > 1 ? "Richtige Antworten" : "Richtige Antwort"}
            </p>
            <ul className="flex flex-col gap-1 text-primary">
              {Array.from({ length: numberOfGaps }, (_, index) => (
                <li key={index}>
                  {numberOfGaps > 1 && (
                    <span className="font-semibold">Lücke {index + 1}: </span>
                  )}
                  {currentEntry.gaps[index]?.referenceAnswer.join(" / ") || "—"}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!isSubmitted && (
          <div className="flex min-w-0 w-full flex-col gap-4">
            <TaskInstructionHint>
              {numberOfGaps > 1
                ? "Fülle die Lücken aus"
                : "Fülle die Lücke aus"}
            </TaskInstructionHint>
            <div className="min-w-0 w-full pl-3 leading-relaxed text-primary">
              {textParts.map((part, index) => {
                const typedLabel = userAnswers[index] || "";
                // Empty gaps follow the authored blank; typed text sizes the
                // control so the sentence can keep using the card width.
                const gapWidthLabel = typedLabel || currentEntry.gapMark;

                return (
                  <Fragment key={index}>
                    {part}
                    {index < textParts.length - 1 && (
                      // Invisible label in normal flow sets the width; the input
                      // is stretched over it so a default browser size cannot
                      // force an early wrap.
                      <span className="relative mx-1 inline-block max-w-full align-baseline">
                        <span
                          aria-hidden="true"
                          className="invisible inline-block whitespace-pre border-2 border-transparent px-3 py-1 font-sans text-base"
                        >
                          {gapWidthLabel}
                        </span>
                        <input
                          type="text"
                          value={typedLabel}
                          onChange={(e) =>
                            handleInputChange(index, e.target.value)
                          }
                          disabled={isValidating}
                          size={1}
                          className={cn(
                            "absolute inset-0 h-full w-full border-2 rounded-md px-3 py-1 text-left font-sans text-base",
                            "focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary",
                            "disabled:cursor-not-allowed transition-colors",
                            getInputStyles(index)
                          )}
                        />
                      </span>
                    )}
                  </Fragment>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="shrink-0 pt-4">
        <div className="flex justify-center">{renderAction()}</div>
      </div>
    </div>
  );
};

export default QuizGapwordFreeText;
